import { Link } from 'react-router-dom';
import { FileText, Mail } from 'lucide-react';

const TermsConditions = () => {
  const lastUpdated = "December 26, 2025";
  
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-medical-primary text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-8 h-8" />
            <h1 className="text-4xl font-heading font-semibold">Terms & Conditions</h1>
          </div>
          <p className="text-white/90">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-sm p-8 space-y-8">
          
          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to Medical Supply and More. These Terms and Conditions ("Terms") govern your use of our website located at medicalsupplyandmore.com and your purchase of products from us. By accessing our website or placing an order, you agree to be bound by these Terms. If you do not agree to these Terms, please do not use our website or services.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">2. Products and Services</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Medical Supply and More sells medical supplies and related products for personal, professional, and institutional use. Our products are intended for their specified purposes as described on our website.
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Product descriptions, images, and specifications are provided for informational purposes and may vary slightly from actual products.</li>
              <li>We reserve the right to modify, discontinue, or limit the availability of any product without prior notice.</li>
              <li>Products are subject to availability, and we cannot guarantee that items will always be in stock.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">3. Medical Disclaimer</h2>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <p className="text-gray-700 leading-relaxed font-medium">
                Important: The products sold on this website are not intended to diagnose, treat, cure, or prevent any disease. The information provided on our website is for general informational purposes only and should not be considered medical advice.
              </p>
            </div>
            <p className="text-gray-700 leading-relaxed">
              Always consult with a qualified healthcare professional before using any medical supplies or equipment. If you have a medical emergency, contact your local emergency services immediately. We are not liable for any health outcomes resulting from the use of products purchased from our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">4. Account Registration</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              To place orders, you may need to create an account. When creating an account, you agree to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide accurate, current, and complete information</li>
              <li>Maintain and update your information to keep it accurate</li>
              <li>Keep your password secure and confidential</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              We reserve the right to suspend or terminate accounts that violate these Terms or engage in fraudulent activity.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">5. Orders and Payment</h2>
            
            <h3 className="text-lg font-semibold mt-4 mb-2">Order Acceptance</h3>
            <p className="text-gray-700 leading-relaxed">
              All orders are subject to acceptance by Medical Supply and More. We reserve the right to refuse or cancel any order for any reason, including but not limited to product availability, errors in pricing or product information, or suspected fraudulent activity.
            </p>

            <h3 className="text-lg font-semibold mt-4 mb-2">Pricing</h3>
            <p className="text-gray-700 leading-relaxed">
              All prices are listed in US Dollars (USD) and are subject to change without notice. We make every effort to ensure pricing accuracy, but errors may occur. If we discover a pricing error after you place an order, we will contact you to confirm whether you wish to proceed at the correct price or cancel your order.
            </p>

            <h3 className="text-lg font-semibold mt-4 mb-2">Payment</h3>
            <p className="text-gray-700 leading-relaxed">
              We accept major credit cards and other payment methods as displayed at checkout. Payment is required at the time of order placement. By providing payment information, you represent that you are authorized to use the payment method and authorize us to charge your payment method for the total order amount.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">6. Shipping and Delivery</h2>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>We currently ship to addresses within the United States.</li>
              <li>Shipping times and costs vary based on your location and selected shipping method.</li>
              <li>Delivery dates are estimates and are not guaranteed.</li>
              <li>Risk of loss and title for products pass to you upon delivery to the carrier.</li>
              <li>You are responsible for providing accurate shipping information. We are not liable for delays or non-delivery due to incorrect addresses.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">7. Returns and Refunds</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We want you to be satisfied with your purchase. Our return policy is as follows:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Eligibility:</strong> Products may be returned within 30 days of delivery if they are unused, unopened, and in original packaging.</li>
              <li><strong>Non-Returnable Items:</strong> For health and safety reasons, certain items cannot be returned once opened, including but not limited to personal care items, hygiene products, and items that have been used or tampered with.</li>
              <li><strong>Damaged or Defective Items:</strong> If you receive a damaged or defective product, please contact us within 7 days of delivery for a replacement or refund.</li>
              <li><strong>Return Process:</strong> To initiate a return, please contact us at info@medicalsupplyandmore.com with your order number and reason for return.</li>
              <li><strong>Refunds:</strong> Approved refunds will be processed to the original payment method within 5-10 business days.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">8. Intellectual Property</h2>
            <p className="text-gray-700 leading-relaxed">
              All content on this website, including text, graphics, logos, images, and software, is the property of Medical Supply and More or its content suppliers and is protected by copyright and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works from any content without our express written permission.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">9. User Conduct</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              When using our website, you agree not to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on the rights of others</li>
              <li>Submit false or misleading information</li>
              <li>Attempt to gain unauthorized access to our systems</li>
              <li>Interfere with the proper functioning of the website</li>
              <li>Use the website for any fraudulent or malicious purpose</li>
              <li>Collect or harvest user data without consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">10. Limitation of Liability</h2>
            <p className="text-gray-700 leading-relaxed">
              To the fullest extent permitted by law, Medical Supply and More shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or other intangible losses, arising out of or related to your use of our website or products. Our total liability for any claim shall not exceed the amount you paid for the product giving rise to the claim.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">11. Disclaimer of Warranties</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website and products are provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant that the website will be uninterrupted, error-free, or free of viruses or other harmful components.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">12. Indemnification</h2>
            <p className="text-gray-700 leading-relaxed">
              You agree to indemnify, defend, and hold harmless Medical Supply and More, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses arising out of your use of the website, violation of these Terms, or infringement of any rights of third parties.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">13. Governing Law</h2>
            <p className="text-gray-700 leading-relaxed">
              These Terms shall be governed by and construed in accordance with the laws of the State of Florida, United States, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of our website shall be resolved exclusively in the courts located in Orange County, Florida.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">14. Changes to Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              We reserve the right to modify these Terms at any time. Changes will be effective immediately upon posting to the website. Your continued use of the website after any changes constitutes your acceptance of the new Terms. We encourage you to review these Terms periodically.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">15. Severability</h2>
            <p className="text-gray-700 leading-relaxed">
              If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
            </p>
          </section>

          <section className="bg-gray-50 rounded-lg p-6">
            <h2 className="text-2xl font-heading font-semibold text-medical-primary mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have any questions about these Terms and Conditions, please contact us at:
            </p>
            <div className="space-y-2 text-gray-700">
              <p className="font-semibold">Medical Supply and More</p>
              <p>Orlando, FL</p>
              <p className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-medical-primary" />
                <a href="mailto:info@medicalsupplyandmore.com" className="text-medical-primary hover:underline">
                  info@medicalsupplyandmore.com
                </a>
              </p>
              <p>Phone: (407) 415-3024</p>
            </div>
          </section>

          <div className="pt-6 border-t text-center">
            <Link to="/" className="text-medical-primary hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditions;
